"use client";

import { Suspense, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Search as SearchIcon, Bell, Lightbulb } from "lucide-react";
import { useSidebar } from "@speakai/ui/sidebar";
import { useAppStore } from "@/stores";
import { useAuth } from "@/hooks/use-auth";
import { useNotifications } from "@/features/notifications/queries";
import { PowerspotPill } from "@/components/layout/powerspot-pill";
import { BookCallButton } from "@/components/layout/book-call-button";
import { BalanceButton, topbarPhoneGhostClasses } from "@/components/layout/balance-button";
import { SpecialOfferPill } from "@/components/layout/special-offer-pill";
import { QuickActionMenu } from "@/components/layout/quick-action-menu";
import { useAccountUsage } from "@/features/dashboard/queries";
import { usePermissions } from "@/hooks/use-permissions";
import { useConversionSurfaceState } from "@/lib/powerspot/use-conversion-surface-state";
import { SubscriptionPlanId } from "@/features/subscription/types";
import { deriveCredits } from "@/features/subscription/hooks/use-credits";
import { useNewTopBar } from "@/config/constants";
import { track } from "@/lib/integrations/sentry-tracking";
import { SUGGESTION_COPY } from "@speakai/common/src/feedback/copy";
import { useSuggestChange } from "@/features/improvements/use-suggest-change";

export function TopBar() {
  if (!useNewTopBar) return null;
  return (
    <Suspense fallback={null}>
      <TopBarInner />
    </Suspense>
  );
}

function TopBarInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collapsed, setCollapsed, setMobileOpen } = useSidebar();
  const { isLoggedIn } = useAuth();
  const setGlobalSearchOpen = useAppStore((s) => s.setGlobalSearchOpen);

  // A stuck-open store makes set(true) a silent no-op — close first so the dialog remounts.
  const openGlobalSearch = () => {
    track("search", "open_spotlight");
    if (useAppStore.getState().globalSearchOpen) {
      setGlobalSearchOpen(false);
      requestAnimationFrame(() => setGlobalSearchOpen(true));
      return;
    }
    setGlobalSearchOpen(true);
  };
  const { data: notificationData } = useNotifications({ page: 0, pageSize: 1 });
  const unreadCount = notificationData?.totalUnReadCount ?? 0;
  const { data: accountUsage } = useAccountUsage();
  const { canManageBilling } = usePermissions();
  const { topbar } = useConversionSurfaceState();
  const suggestChange = useSuggestChange();

  // UI-CHROME-ONLY: server RBAC is authoritative; BalanceButton re-checks this itself.
  // Mirrors BalanceButton's own cohort gate. Expired trials count: topping up is how a
  // lapsed account reactivates. Stands down for low_balance, whose pill is a top-up CTA.
  const planId = accountUsage?.planId;
  const { creditMode } = deriveCredits(accountUsage);
  const showCreditsPill =
    canManageBilling &&
    (planId === SubscriptionPlanId.Speak_Free_Trial ||
      planId === SubscriptionPlanId.Speak_Pay_As_You_Go ||
      creditMode) &&
    topbar.variant !== "low_balance";

  // Drives the --topbar-height CSS variable in globals.css. Without this
  // attribute the variable resolves to flag-off geometry so a rollback of
  // useNewTopBar restores prior viewport math on chat / media-editor /
  // media-insight pages.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-topbar", "on");
    return () => {
      root.removeAttribute("data-topbar");
    };
  }, []);

  // Force-uncollapse on mobile. The @speakai/ui Sidebar shares one `collapsed`
  // state between desktop sidebar + mobile drawer — without this, a user who
  // collapsed on desktop hits a brutally-narrow icons-only drawer on mobile.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 767px)");
    const sync = () => { if (mql.matches && collapsed) setCollapsed(false); };
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, [collapsed, setCollapsed]);

  if (!isLoggedIn) return null;

  // Display utility lives at the call site so callers can use `hidden md:inline-flex`
  // without the trailing `inline-flex` clobbering `hidden` (same-specificity, source-order).
  const iconBtn =
    `h-10 w-10 pointer-coarse:h-11 pointer-coarse:w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${topbarPhoneGhostClasses}`;

  return (
    <header
      role="banner"
      aria-label="Global"
      style={{ height: "var(--topbar-height)" }}
      className="shrink-0 z-30 bg-card border-b border-border flex items-center"
      data-testid="top-bar"
    >
      {/* Brand zone — fixed width on md+ to align search edge with sidebar content boundary */}
      <div className="flex items-center gap-1 ps-2 pe-0 sm:gap-2 sm:px-4 md:w-[240px] md:px-4 shrink-0">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className={`inline-flex md:hidden ${iconBtn}`}
          aria-label={t("ACTION.OPEN_MENU")}
          data-testid="topbar-mobile-menu"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="flex items-center shrink-0 pr-2" data-testid="topbar-logo">
          <Link
            to="/home"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center"
            aria-label={t("MENU.SPEAK_HOME", "Speak AI home")}
          >
            <span className="text-xl font-bold tracking-tight text-foreground">Speak AI</span>
          </Link>
        </div>
      </div>

      {/* Content zone — search, powerspot, quick-action, help, bell */}
      <div className="flex flex-1 min-w-0 items-center gap-1 sm:gap-2 ps-0 pe-2 sm:px-4">
        <button
          type="button"
          onClick={openGlobalSearch}
          className={`inline-flex lg:hidden ${iconBtn}`}
          aria-label={t("ACTION.SEARCH")}
          data-testid="topbar-search-icon"
        >
          <SearchIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={openGlobalSearch}
          className="hidden lg:inline-flex min-h-10 items-center gap-2 rounded-lg border border-border bg-muted/40 hover:bg-muted px-3 py-1.5 text-sm text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-w-[240px]"
          aria-label={t("ACTION.SEARCH")}
          data-testid="topbar-search"
        >
          <SearchIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 text-left">{t("ACTION.SEARCH")}</span>
          <kbd className="pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-0.5 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>

        {/* Below sm the spacer sorts first so search joins the right-hand icon group. */}
        <div className="flex-1 min-w-0 max-sm:-order-1" />

        <div data-job-status-slot className="hidden md:block empty:hidden" />

        <div data-special-offer-slot className="hidden sm:block shrink-0 empty:hidden">
          <SpecialOfferPill />
        </div>

        {/* Ordering stays local: an order-* out here would sort both pills past the bell. */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {showCreditsPill && (
            <div data-trial-credits-slot className="hidden sm:block shrink-0 empty:hidden md:order-2">
              <BalanceButton source="topbar_trial_add_credits" />
            </div>
          )}
          <div data-powerspot-slot className="shrink-0 empty:hidden md:order-1">
            <PowerspotPill />
          </div>
          <div data-book-call-slot className="shrink-0 empty:hidden md:order-3">
            <BookCallButton />
          </div>
        </div>

        <QuickActionMenu variant="outline" hideLabelOnMobile />

        {suggestChange.enabled && (
          <button
            type="button"
            onClick={suggestChange.open}
            className={`relative hidden sm:inline-flex ${iconBtn}`}
            aria-label={t("IMPROVEMENTS.SUGGEST_CHANGE", SUGGESTION_COPY.entryLabel)}
            title={t("IMPROVEMENTS.SUGGEST_CHANGE", SUGGESTION_COPY.entryLabel)}
            aria-expanded={suggestChange.isOpen}
            data-testid="topbar-suggest-change"
          >
            <Lightbulb className="h-4 w-4" aria-hidden="true" />
            {!suggestChange.seen && (
              <span className="pointer-events-none absolute top-2 end-2 h-2 w-2 rounded-full bg-primary ring-2 ring-card" data-testid="topbar-suggest-change-new" />
            )}
          </button>
        )}

        <button
          type="button"
          onClick={() => navigate("/notifications")}
          className={`relative inline-flex ${iconBtn}`}
          aria-label={unreadCount > 0
            ? t("MENU.NOTIFICATIONS_WITH_UNREAD", {
                defaultValue: "{{label}}, {{count}} unread",
                label: t("MENU.NOTIFICATIONS"),
                count: unreadCount,
              })
            : t("MENU.NOTIFICATIONS")}
          data-testid="topbar-bell"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="pointer-events-none absolute top-0 end-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-xs font-semibold leading-none text-destructive-foreground ring-2 ring-card">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>
    </header>
  );
}
